/**
 * Screen Context Registry
 *
 * Lets pages/plugins volunteer a richer description of "what's on screen"
 * than the bare entity pointer in `PageActionContext` — e.g. a note's
 * title + excerpt, the currently selected rows in a table, a form's
 * validation state. The assistant overlay composes these contributions
 * into the per-turn context it hands the model.
 *
 * Contributions are pull-based (a `getContext()` function called on demand)
 * so they always reflect current state without the contributor having to
 * push updates.
 *
 * No domain logic — pure infrastructure.
 */

import { create } from 'zustand';

export interface ScreenContextResult {
  /** Short human-readable summary of what's on screen (shown to the model, and to the user via the context badge). */
  summary: string;
  /** Optional structured data for the model. */
  data?: Record<string, unknown>;
}

export interface ScreenContextContributor {
  /** Unique contributor ID, e.g. "notes.detail-page" */
  id: string;
  /** Plugin (or "core") that registered this contributor */
  pluginId: string;
  /**
   * Page ID patterns this contributor applies to (same glob syntax as
   * `PageAction.pageIds`). If omitted, applies to every page.
   */
  pageIds?: string[];
  /** Called to produce the current context snapshot. May throw — callers must guard. */
  getContext: () => ScreenContextResult | null;
}

interface ScreenContextRegistryState {
  contributors: Map<string, ScreenContextContributor>;
  register: (contributor: ScreenContextContributor) => void;
  unregister: (id: string) => void;
  unregisterPlugin: (pluginId: string) => void;
  /**
   * Get context from every contributor that matches the given page ID —
   * only those registered by one of `pluginIds`, when given.
   */
  getContextForPage: (pageId: string, pluginIds?: readonly string[]) => ScreenContextResult[];
}

function matchPageId(pagePath: string, pattern: string): boolean {
  if (pattern === pagePath) return true;
  if (pattern.endsWith('/**')) {
    const prefix = pattern.slice(0, -3);
    return pagePath === prefix || pagePath.startsWith(prefix + '/');
  }
  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -2);
    if (!pagePath.startsWith(prefix + '/')) return false;
    const remainder = pagePath.slice(prefix.length + 1);
    return remainder.length > 0 && !remainder.includes('/');
  }
  return false;
}

export const useScreenContextRegistry = create<ScreenContextRegistryState>()((set, get) => ({
  contributors: new Map(),

  register: (contributor) => {
    set((state) => {
      const next = new Map(state.contributors);
      next.set(contributor.id, contributor);
      return { contributors: next };
    });
  },

  unregister: (id) => {
    set((state) => {
      if (!state.contributors.has(id)) return state;
      const next = new Map(state.contributors);
      next.delete(id);
      return { contributors: next };
    });
  },

  unregisterPlugin: (pluginId) => {
    set((state) => {
      const next = new Map(state.contributors);
      for (const [id, c] of next) {
        if (c.pluginId === pluginId) next.delete(id);
      }
      return { contributors: next };
    });
  },

  getContextForPage: (pageId, pluginIds) => {
    const results: ScreenContextResult[] = [];
    for (const contributor of get().contributors.values()) {
      if (pluginIds && !pluginIds.includes(contributor.pluginId)) continue;
      if (contributor.pageIds && contributor.pageIds.length > 0) {
        const matches = contributor.pageIds.some((p) => matchPageId(pageId, p));
        if (!matches) continue;
      }
      try {
        const result = contributor.getContext();
        if (result) results.push(result);
      } catch (err) {
        console.error(`[ScreenContextRegistry] Contributor "${contributor.id}" threw:`, err);
      }
    }
    return results;
  },
}));
